import { useEffect, useRef, useState } from 'react'
import { fetchAllProducts } from '../api/catalog'
import { contractsApi } from '../api/contracts'
import { documentsApi } from '../api/documents'
import { unoMobileApi } from '../api/unomobile'
import { Button, Callout, Card, CardContent, CardHeader, Checkbox, Select, TextField } from './ui'

/**
 * Attivazione guidata sul portale 1Mobile: la sessione del portale resta aperta sul server e l'operatore la porta
 * avanti passo per passo. Il captcha lo legge e lo scrive sempre l'operatore; ogni passo che scrive sul portale
 * parte solo da un suo clic. La firma si fa qui (dito o mouse) e finisce in ogni spazio firma del contratto stampato,
 * con "Grammichele" e la data di oggi (giorno del caricamento sul portale) in ogni spazio DATA.
 */

type Step = 'closed' | 'search' | 'captcha' | 'sim' | 'number' | 'mnp' | 'offer' | 'consents' | 'sign' | 'upload' | 'done'

const errorText = (err: unknown) => (err instanceof Error ? err.message : String(err))
const today = () => new Date().toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' })

function base64ToFile(b64: string, name: string, type: string) {
  const bin = atob(b64)
  const bytes = new Uint8Array(bin.length)
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i)
  return new File([bytes], name, { type })
}

/** Piani tariffari del portale 1Mobile; le offerte (Speed 5G 250, Flash 120...) sono i prodotti del catalogo CRM. */
const PORTAL_PLANS = ['1Mobile 9', '1Mobile Senza Scatto']

export function ContractUnoMobileActivation({ contractId, operatorId, productId }: {
  contractId: number; operatorId?: number | null; productId?: number | null
}) {
  const [step, setStep] = useState<Step>('closed')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [portal, setPortal] = useState<Record<string, string>>({})

  const [fiscalCode, setFiscalCode] = useState('')
  const [draftId, setDraftId] = useState('')
  const [captcha, setCaptcha] = useState<string | null>(null)
  const [answer, setAnswer] = useState('')
  const [iccid, setIccid] = useState('')
  const [numbers, setNumbers] = useState<{ value: string; label: string }[]>([])
  const [number, setNumber] = useState('')
  const [operators, setOperators] = useState<string[]>([])
  const [donorMsisdn, setDonorMsisdn] = useState('')
  const [donorOperator, setDonorOperator] = useState('')
  const [prepaid, setPrepaid] = useState(true)
  // Il trasferimento del credito residuo va chiesto sempre: casella attiva di default.
  const [transferCredit, setTransferCredit] = useState(true)
  const [tariffPlan, setTariffPlan] = useState(PORTAL_PLANS[0])
  const [options, setOptions] = useState<string[]>([])
  const [offers, setOffers] = useState<string[]>([])
  const [friendCode, setFriendCode] = useState('')
  const [agree, setAgree] = useState(true)
  const [pdfDocId, setPdfDocId] = useState<number | null>(null)
  const [pdfB64, setPdfB64] = useState<string | null>(null)
  const [screenshot, setScreenshot] = useState<string | null>(null)

  const canvasRef = useRef<HTMLCanvasElement | null>(null)
  const drawing = useRef(false)
  const [signed, setSigned] = useState(false)

  const portability = portal['TIPO_ATTIVAZIONE'] === 'PORTABILITA'

  useEffect(() => {
    let alive = true
    unoMobileApi.activationStatus(contractId)
      .then(s => { if (alive && s.open) setStep('search') })
      .catch(() => { /* sessione non aperta */ })
    contractsApi.getPortalData(contractId)
      .then(d => {
        if (!alive || !d) return
        setPortal(d)
        setFiscalCode(d['CODICE_FISCALE_CLIENTE'] || d['PARTITA_IVA_CLIENTE'] || '')
        setIccid(d['SERIALE_SIM'] || '')
        setDonorMsisdn(d['NUMERO_DA_PORTARE'] || '')
        // dati chiesti nella pratica (scheda SIM): il trasferimento del credito resta attivo se non e' "NO"
        setPrepaid(d['TIPO_CONTRATTO_ATTUALE'] !== 'ABBONAMENTO')
        setTransferCredit(d['TRASFERIMENTO_CREDITO'] !== 'NO')
      })
      .catch(() => { /* dati portale facoltativi */ })
    if (operatorId) {
      fetchAllProducts({ operatorId })
        .then(ps => {
          if (!alive) return
          // l'offerta venduta nella pratica parte gia' spuntata
          setOffers(ps.items.filter(p => p.isActive).map(p => p.name))
          const sold = ps.items.find(p => p.id === productId)
          if (sold) setOptions([sold.name])
        })
        .catch(() => { /* catalogo facoltativo: si scrive a mano */ })
    }
    return () => { alive = false }
  }, [contractId, operatorId, productId])

  async function run<T>(fn: () => Promise<T>): Promise<T | undefined> {
    setBusy(true)
    setError(null)
    try { return await fn() }
    catch (err) { setError(errorText(err)); return undefined }
    finally { setBusy(false) }
  }

  const cmd = <T,>(name: string, body: Record<string, unknown> = {}) =>
    unoMobileApi.activationCmd<T>(contractId, name, body)

  const start = () => run(async () => {
    await unoMobileApi.activationStart(contractId)
    setMessage(null)
    setStep('search')
  })

  const close = () => run(async () => {
    await unoMobileApi.activationClose(contractId)
    setStep('closed')
    setCaptcha(null)
    setScreenshot(null)
  })

  const search = () => run(async () => {
    const r = await cmd<{ captcha: string }>('search', draftId.trim()
      ? { subscriptionId: draftId.trim() }
      : { fiscalCode: fiscalCode.trim().toUpperCase() })
    setCaptcha(r.captcha)
    setAnswer('')
    setStep('captcha')
  })

  const sendCaptcha = () => run(async () => {
    const r = await cmd<{ retry?: boolean; captcha?: string; records?: number; message?: string }>('captcha', { answer: answer.trim() })
    setAnswer('')
    if (r.retry && r.captcha) {
      setCaptcha(r.captcha)
      setError('Captcha non corretto: ne è arrivato uno nuovo.')
      return
    }
    setCaptcha(null)
    setMessage(r.message ?? null)
    if (draftId.trim()) {
      // Bozza gia' aperta sul portale: si riprende dal passo 3 senza crearne una nuova.
      await cmd('resume')
      setStep('sim')
      return
    }
    const s2 = await cmd<{ error?: string }>('step2')
    if (s2.error) { setError(s2.error); return }
    setStep('sim')
  })

  const verifySim = () => run(async () => {
    const r = await cmd<{ numbers: { value: string; label: string }[]; message?: string }>('sim', { iccid: iccid.trim() })
    setNumbers(r.numbers ?? [])
    setNumber(r.numbers?.[0]?.value ?? '')
    setMessage(r.message ?? null)
    setStep('number')
  })

  const assign = () => run(async () => {
    const r = await cmd<{ message?: string }>('assign', { number })
    setMessage(r.message ?? null)
    if (portability) {
      const o = await cmd<{ operators: string[] }>('operators')
      setOperators(o.operators ?? [])
      const current = (portal['OPERATORE_ATTUALE'] ?? '').toLowerCase()
      setDonorOperator(o.operators?.find(x => current && x.toLowerCase().includes(current)) ?? '')
      setStep('mnp')
    } else {
      setStep('offer')
    }
  })

  const mnp = () => run(async () => {
    const r = await cmd<{ error?: string; message?: string }>('mnp', {
      donorMsisdn: donorMsisdn.trim(), donorOperator, prepaid, transferCredit,
    })
    if (r.error) { setError(r.error); return }
    setMessage(r.message ?? null)
    setStep('offer')
  })

  const offer = () => run(async () => {
    const r = await cmd<{ message?: string }>('offer', { tariffPlan: tariffPlan.trim(), options })
    setMessage(r.message ?? null)
    setStep('consents')
  })

  const consents = () => run(async () => {
    await cmd('consents', { friendCode: friendCode.trim(), agree })
    const r = await cmd<{ step4?: boolean; error?: string; message?: string }>('submit3')
    if (r.error || !r.step4) { setError(r.error || r.message || 'Il portale non è passato al caricamento documenti.'); return }
    setMessage(r.message ?? null)
    setStep('sign')
  })

  // --- firma ---
  function point(e: React.PointerEvent<HTMLCanvasElement>) {
    const c = e.currentTarget
    const rect = c.getBoundingClientRect()
    return { x: (e.clientX - rect.left) * (c.width / rect.width), y: (e.clientY - rect.top) * (c.height / rect.height) }
  }
  function down(e: React.PointerEvent<HTMLCanvasElement>) {
    const ctx = e.currentTarget.getContext('2d')
    if (!ctx) return
    e.currentTarget.setPointerCapture(e.pointerId)
    drawing.current = true
    const p = point(e)
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    ctx.strokeStyle = '#0b2a6f'
    ctx.beginPath()
    ctx.moveTo(p.x, p.y)
  }
  function move(e: React.PointerEvent<HTMLCanvasElement>) {
    if (!drawing.current) return
    const ctx = e.currentTarget.getContext('2d')
    if (!ctx) return
    const p = point(e)
    ctx.lineTo(p.x, p.y)
    ctx.stroke()
    setSigned(true)
  }
  function up() { drawing.current = false }
  function clearSignature() {
    const c = canvasRef.current
    c?.getContext('2d')?.clearRect(0, 0, c.width, c.height)
    setSigned(false)
  }

  const printContract = () => run(async () => {
    const c = canvasRef.current
    if (!c) return
    const png = c.toDataURL('image/png').split(',')[1]
    const date = today()
    await documentsApi.upload('Contract', contractId, base64ToFile(png, 'firma-cliente.png', 'image/png'),
      `Firma cliente per attivazione 1Mobile (${date})`)
    const r = await cmd<{ pdf: string; signatures?: number; dates?: number }>('contract', {
      signaturePng: png, place: 'Grammichele', date,
    })
    const doc = await documentsApi.upload('Contract', contractId,
      base64ToFile(r.pdf, `contratto-1mobile-firmato-${date.replace(/\//g, '-')}.pdf`, 'application/pdf'),
      `Contratto 1Mobile firmato: ${r.signatures ?? 0} firme, ${r.dates ?? 0} date (Grammichele, ${date})`)
    setPdfB64(r.pdf)
    setPdfDocId(doc.id)
    setMessage(`Contratto firmato salvato tra i documenti (${r.signatures ?? 0} firme, ${r.dates ?? 0} date).`)
    setStep('upload')
  })

  const uploadAndFinish = () => run(async () => {
    if (!pdfB64) return
    const u = await cmd<{ uploaded?: boolean; message?: string }>('upload', {
      name: `contratto-firmato-${contractId}.pdf`, contentBase64: pdfB64,
    })
    if (!u.uploaded) { setError(u.message || 'Caricamento sul portale non riuscito.'); return }
    const f = await cmd<{ message?: string }>('finish')
    setMessage(f.message ?? 'Attivazione conclusa sul portale.')
    setStep('done')
  })

  const takeScreenshot = () => run(async () => {
    const r = await cmd<{ image: string }>('screenshot')
    setScreenshot(r.image)
  })

  const subtitle = step === 'closed' ? 'Apri la sessione del portale per attivare la SIM da qui'
    : step === 'done' ? 'Attivazione conclusa' : 'Sessione del portale aperta (si chiude dopo 15 minuti di inattività)'

  return (
    <Card>
      <CardHeader icon="sim_card" title="Attivazione sul portale 1Mobile" subtitle={subtitle}
        actions={step !== 'closed' ? (
          <>
            <Button variant="text" size="sm" icon="screenshot_monitor" onClick={takeScreenshot} disabled={busy}>Schermata</Button>
            <Button variant="text" size="sm" tone="error" icon="close" onClick={close} disabled={busy}>Chiudi sessione</Button>
          </>
        ) : undefined} />
      <CardContent>
        <div style={{ display: 'grid', gap: 12 }}>
          {error && <Callout tone="error" icon="error">{error}</Callout>}
          {message && <Callout tone="info" icon="info">{message}</Callout>}

          {step === 'closed' && (
            <div><Button variant="filled" icon="login" loading={busy} onClick={start}>Attiva sul portale</Button></div>
          )}

          {step === 'search' && (
            <>
              <TextField label="Codice fiscale o partita IVA del cliente" uppercase value={fiscalCode}
                disabled={!!draftId.trim()} onChange={e => setFiscalCode(e.target.value)} />
              <TextField label="Oppure ID abbonamento di una bozza da riprendere" value={draftId} inputMode="numeric"
                supportingText="Se la pratica è già aperta sul portale, riprendila da qui: non se ne crea un doppione."
                onChange={e => setDraftId(e.target.value.replace(/\D/g, ''))} />
              <div>
                <Button variant="filled" icon="search" loading={busy} disabled={!fiscalCode.trim() && !draftId.trim()} onClick={search}>
                  {draftId.trim() ? 'Cerca bozza' : 'Cerca cliente'}
                </Button>
              </div>
            </>
          )}

          {step === 'captcha' && captcha && (
            <>
              <img src={`data:image/png;base64,${captcha}`} alt="Captcha del portale"
                style={{ maxWidth: 260, border: '1px solid var(--md-sys-color-outline-variant, #ccc)', borderRadius: 8 }} />
              <TextField label="Scrivi i caratteri del captcha" value={answer} autoComplete="off"
                onChange={e => setAnswer(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && answer.trim() && !busy) void sendCaptcha() }} />
              <div><Button variant="filled" icon="check" loading={busy} disabled={!answer.trim()} onClick={sendCaptcha}>Conferma</Button></div>
            </>
          )}

          {step === 'sim' && (
            <>
              <TextField label="ICCID della SIM" value={iccid} onChange={e => setIccid(e.target.value)} />
              <div><Button variant="filled" icon="sim_card" loading={busy} disabled={!iccid.trim()} onClick={verifySim}>Verifica SIM</Button></div>
            </>
          )}

          {step === 'number' && (
            <>
              <Select label="Numero da assegnare" value={number} onChange={e => setNumber(e.target.value)}>
                {numbers.map(n => <option key={n.value} value={n.value}>{n.label}</option>)}
              </Select>
              <div><Button variant="filled" icon="call" loading={busy} disabled={!number} onClick={assign}>Assegna numero</Button></div>
            </>
          )}

          {step === 'mnp' && (
            <>
              <TextField label="Numero da portare" value={donorMsisdn} onChange={e => setDonorMsisdn(e.target.value)} />
              <Select label="Operatore attuale" value={donorOperator} onChange={e => setDonorOperator(e.target.value)}>
                <option value="">Scegli…</option>
                {operators.map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
              <Checkbox label="SIM ricaricabile" checked={prepaid} onChange={e => setPrepaid(e.target.checked)} />
              <Checkbox label="Trasferisci il credito residuo" checked={transferCredit} onChange={e => setTransferCredit(e.target.checked)} />
              <div><Button variant="filled" icon="swap_horiz" loading={busy} disabled={!donorMsisdn.trim() || !donorOperator} onClick={mnp}>Conferma portabilità</Button></div>
            </>
          )}

          {step === 'offer' && (
            <>
              <TextField label="Piano tariffario (come sul portale)" value={tariffPlan} list="unomobile-plans"
                onChange={e => setTariffPlan(e.target.value)} />
              <datalist id="unomobile-plans">{PORTAL_PLANS.map(p => <option key={p} value={p} />)}</datalist>
              {offers.length > 0 && (
                <div style={{ display: 'grid', gap: 4 }}>
                  <div>Offerte</div>
                  {offers.map(o => (
                    <Checkbox key={o} label={o} checked={options.includes(o)}
                      onChange={e => setOptions(cur => e.target.checked ? [...cur, o] : cur.filter(x => x !== o))} />
                  ))}
                </div>
              )}
              <div><Button variant="filled" icon="sell" loading={busy} disabled={!tariffPlan.trim()} onClick={offer}>Scegli offerta</Button></div>
            </>
          )}

          {step === 'consents' && (
            <>
              <TextField label="Codice amico (facoltativo)" uppercase value={friendCode} onChange={e => setFriendCode(e.target.value)} />
              <Checkbox label="Il cliente ha dato i consensi" checked={agree} onChange={e => setAgree(e.target.checked)} />
              <div><Button variant="filled" icon="arrow_forward" loading={busy} onClick={consents}>Conferma e vai ai documenti</Button></div>
            </>
          )}

          {step === 'sign' && (
            <>
              <div>Firma del cliente (dito o mouse):</div>
              <canvas ref={canvasRef} width={600} height={200}
                onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up}
                style={{ width: '100%', maxWidth: 600, aspectRatio: '3 / 1', touchAction: 'none', background: '#fff',
                  border: '1px dashed var(--md-sys-color-outline, #888)', borderRadius: 8 }} />
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <Button variant="outlined" icon="ink_eraser" onClick={clearSignature} disabled={busy || !signed}>Cancella</Button>
                <Button variant="filled" icon="draw" loading={busy} disabled={!signed} onClick={printContract}>
                  Stampa contratto firmato (Grammichele, {today()})
                </Button>
              </div>
            </>
          )}

          {step === 'upload' && (
            <>
              {pdfDocId && <div>Il contratto firmato è tra i documenti della pratica.</div>}
              <div><Button variant="filled" icon="upload" loading={busy} onClick={uploadAndFinish}>Carica sul portale e concludi</Button></div>
            </>
          )}

          {screenshot && (
            <img src={`data:image/jpeg;base64,${screenshot}`} alt="Schermata del portale"
              style={{ width: '100%', border: '1px solid var(--md-sys-color-outline-variant, #ccc)', borderRadius: 8 }} />
          )}
        </div>
      </CardContent>
    </Card>
  )
}
